import React, { useEffect, useState } from "react";
import SubmitButton from "../../components/partials/global/SubmitButton";
import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Mail,
  MapPin,
  Package,
  Plane,
  Truck,
} from "lucide-react";
import { showError, showSuccess } from "../../utils/toastUtils";
import warehouseAPI from "../../api/inventory/warehouseApi";
import shippingAPI from "../../api/shippingApi";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Link } from "react-router-dom";

const STEP_FORM = "form"; // box details fill karo
const STEP_RATES = "rates"; // rates dekho, select karo
const STEP_DONE = "done"; // shipment created

function ShipmentDetail({ item }) {
  const [step, setStep] = useState(STEP_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLogs, setTrackingLogs] = useState([]);

  const INITIAL_FORM_DATA = {
    height: "",
    width: "",
    length: "",
    weight: "",
    mobile: "",
    pickupAddress: "",
    paymentMode:
      item?.payment?.method?.toLowerCase() === "cod" ||
      item?.payment?.method?.toLowerCase() === "partial"
        ? "cod"
        : "prepaid",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesError, setRatesError] = useState("");

  useEffect(() => {
    setStep(STEP_FORM);
    setRates([]);
    setSelectedRate(null);
    setRatesError("");
    setErrors({});
    setFormData((prev) => ({
      ...prev,
      height: item?.packageDetails?.height || "",
      width: item?.packageDetails?.breadth || "",
      length: item?.packageDetails?.length || "",
      weight: item?.packageDetails?.weight
        ? item?.packageDetails?.weight * 1000
        : "",
      paymentMode:
        item?.payment?.method?.toLowerCase() === "cod" ||
        item?.payment?.method?.toLowerCase() === "partial"
          ? "cod"
          : "prepaid",
    }));

    if (item?.statusHistoryAwb?.awb) {
      setStep(STEP_DONE);
      fetchTrackingLogs();
    }
  }, [item]);

  /******* fetch warehouses ****** */
  useEffect(() => {
    fetchWarehouseData();
  }, []);

  const fetchWarehouseData = async () => {
    try {
      const res = await warehouseAPI.list();

      if (res.code === 200) {
        const result = res.data;
        setAddresses(result.warehouses);
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (addresses.length > 0 && !formData.pickupAddress) {
      setFormData((prev) => ({ ...prev, pickupAddress: addresses[0]._id }));
    }
  }, [addresses]);

  /******* update form data ****** */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /******* validation ****** */
  const validate = () => {
    const newErrors = {};
    if (!formData.width) newErrors.width = "Width is required";
    if (!formData.height) newErrors.height = "Height is required";
    if (!formData.length) newErrors.length = "Length is required";
    if (!formData.weight) newErrors.weight = "Weight is required";
    if (!formData.pickupAddress)
      newErrors.pickupAddress = "Select a pickup address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /******* get rates  ****** */
  const handleGetRates = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setRatesError("");

    const selectedWarehouse = addresses.find(
      (a) => a._id === formData.pickupAddress,
    );

    const payload = {
      pickup: {
        pincode: selectedWarehouse?.pincode || selectedWarehouse?.pinCode,
        city: selectedWarehouse?.city,
        state: selectedWarehouse?.state,
      },
      delivery: {
        pincode:
          item?.isShippingSameasBilling === false
            ? item?.shippingAddress?.pinCode
            : item?.address?.pinCode,
        city:
          item?.isShippingSameasBilling === false
            ? item?.shippingAddress?.city
            : item?.address?.city,
        state:
          item?.isShippingSameasBilling === false
            ? item?.shippingAddress?.state
            : item?.address?.state,
      },
      packageDetails: {
        length: Number(formData.length),
        breadth: Number(formData.width),
        height: Number(formData.height),
        weight: Number(formData.weight) / 1000,
      },
      paymentMode: formData.paymentMode,
      orderValue: Number(item?.total || 0),
      orderId: item?.id || item?._id,
    };

    try {
      const res = await shippingAPI.getRates(payload);
      if (res.code === 200) {
        const rateList = res.data?.rates || res.data || [];
        if (rateList.length === 0) {
          setRatesError("No courier rates available for the given details");
        } else {
          setRates(rateList);
          setSelectedRate(rateList[0]);
          setStep(STEP_RATES);
        }
      } else {
        setRatesError(res.message || "Failed to fetch rates. Please retry.");
      }
    } catch (err) {
      handleApiValidationError?.(err, setErrors);
      console.log(errors);
    } finally {
      setIsLoading(false);
    }
  };

  /******* create shipment  ****** */
  const handleCreateShipment = async () => {
    if (!selectedRate) return;
    setIsLoading(true);

    const payload = {
      orderId: item?._id || item?.id,
      providerName: selectedRate.providerName || selectedRate.provider,
      packageDetails: {
        length: Number(formData.length),
        breadth: Number(formData.width),
        height: Number(formData.height),
        weight: Number(formData.weight),
      },
      paymentMode: formData.paymentMode,
      courierName: selectedRate.courierName || selectedRate.name,
      rateId: selectedRate.rateId || selectedRate.id,
      pickup: formData.pickupAddress,
    };

    try {
      const res = await shippingAPI.createShipment(payload);
      if (res.code === 200) {
        showSuccess("Shipment created successfully");
        setStep(STEP_DONE);

        setTimeout(() => {
          document.getElementById("offcanvasCloser")?.click();
        }, 3000);
      } else {
        showError(res.message || "Something went wrong");
      }
    } catch (err) {
      handleApiValidationError?.(err, setErrors);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrackingLogs = async () => {
    try {
      const res = await shippingAPI.trackShipment(
        item?.statusHistoryAwb?.shipmentLogId,
      );
      if (res.code === 200) {
        setTrackingLogs(res.data?.tracking?.timeline || []);
        setTrackingData(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const customerName =
    `${item?.userId?.firstName || item?.user?.firstName || ""} ${
      item?.userId?.lastName || item?.user?.lastName || ""
    }`.trim();

  return (
    <>
      <div className="offcanvas-header">
        <div>
          <h5 className="mb-0" id="offcanvasRightLabel">
            Shipment Details
          </h5>
          <small className="text-muted">#{item?.orderId}</small>
        </div>
        <button
          type="button"
          className="btn-close text-reset"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          id="offcanvasCloser"
        />
      </div>

      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-light">
        <StepBadge
          n={1}
          label="Details"
          active={step === STEP_FORM}
          done={step !== STEP_FORM}
        />
        <ChevronRight size={14} className="text-muted" />
        <StepBadge
          n={2}
          label="Select Rate"
          active={step === STEP_RATES}
          done={step === STEP_DONE}
        />
        <ChevronRight size={14} className="text-muted" />
        <StepBadge
          n={3}
          label="Confirm"
          active={step === STEP_DONE}
          done={false}
        />
      </div>

      {/* end offcanvas-header*/}
      <div className="offcanvas-body">
        {step === STEP_FORM && (
          <form onSubmit={handleGetRates}>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="row mb-2">
                <div className="col-md-12">
                  <small className="text-muted d-flex align-items-center gap-1">
                    <Package size={13} />
                    Customer
                  </small>
                  <div className="fw-semibold small">{customerName}</div>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <small className="text-muted d-flex align-items-center gap-1">
                    <Mail size={13} />
                    Email
                  </small>
                  <div className="fw-semibold small">
                    {item?.userId?.email || item.user?.email}
                  </div>
                </div>
              </div>

              {(() => {
                const targetAddress =
                  item?.isShippingSameasBilling === false
                    ? item?.shippingAddress
                    : item?.address;

                if (!targetAddress) return null;

                const addressParts = [
                  targetAddress.addressLine1,
                  targetAddress.addressLine2,
                  targetAddress.district,
                  targetAddress.state,
                ].filter(Boolean);

                return (
                  <div className="mb-2">
                    <small className="text-muted d-flex align-items-center gap-1">
                      <MapPin size={13} />
                      {item?.isShippingSameasBilling === false
                        ? "Shipping Address"
                        : "Billing Address"}
                    </small>
                    <div className="fw-semibold small">
                      {addressParts.join(", ")}
                      {targetAddress.pinCode && ` — ${targetAddress.pinCode}`}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="row">
              <div className="col-md-12">
                <h6>Box Details</h6>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Width (cm) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="width"
                  className="form-control"
                  placeholder="Width (cm)"
                  value={formData.width}
                  onChange={handleChange}
                />
                {errors.width && (
                  <span className="errors text-danger">{errors.width}</span>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Height (cm) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="height"
                  className="form-control"
                  placeholder="Height (cm)"
                  value={formData.height}
                  onChange={handleChange}
                />
                {errors.height && (
                  <span className="errors text-danger">{errors.height}</span>
                )}
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-6">
                <label className="form-label">
                  Length (cm) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="length"
                  className="form-control"
                  placeholder="length (cm)"
                  value={formData.length}
                  onChange={handleChange}
                />
                {errors.length && (
                  <span className="errors text-danger">{errors.length}</span>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Weight (g) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="weight"
                  className="form-control"
                  placeholder="weight (g)"
                  value={formData.weight}
                  onChange={handleChange}
                />
                {errors.weight && (
                  <span className="errors text-danger">{errors.weight}</span>
                )}
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-12">
                <label className="form-label mb-1">Payment Mode</label>
                <select
                  name="paymentMode"
                  className="form-control"
                  value={formData.paymentMode}
                  onChange={handleChange}
                >
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-12 mb-1">
                <h6>Pickup Address</h6>
              </div>
              <div className="col-md-12">
                {addresses.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No pickup addresses added yet
                  </div>
                ) : (
                  addresses.map((item) => (
                    <label
                      key={item._id}
                      className={`border rounded p-3 mb-2 d-flex align-items-start cursor-pointer ${
                        formData.pickupAddress === item._id
                          ? "border-primary bg-light"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="me-2">
                        <input
                          type="radio"
                          name="pickupAddress"
                          value={item._id}
                          checked={formData.pickupAddress === item._id}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <div className="fw-bold text-dark">
                          {item.wareHouseName} ({item.wareHouseId})
                        </div>
                        <div className="text-muted small">
                          {item.mobile}, {item.email}
                        </div>
                        <div className="small text-dark">
                          {item.addressLine1}, {item.area}, {item.city},{" "}
                          {item.state}, - {item.pincode}, {item.country}
                        </div>
                      </div>
                    </label>
                  ))
                )}
                {errors.pickupAddress && (
                  <div className="text-danger small">
                    {errors.pickupAddress}
                  </div>
                )}
              </div>
            </div>

            {ratesError && (
              <div className="alert alert-danger py-2 small d-flex align-items-center gap-2">
                <AlertCircle size={16} /> {ratesError}
              </div>
            )}

            {errors.general && (
              <div className="alert alert-danger py-2 small d-flex align-items-center gap-2">
                <AlertCircle size={16} /> {errors.general}
              </div>
            )}

            <div className="row mt-3">
              <div className="col-12 d-flex justify-content-end">
                <SubmitButton
                  isLoading={isLoading}
                  text="Get Shipping Rates"
                  loadingText="Fetching Rates..."
                  className="w-100"
                ></SubmitButton>
              </div>
            </div>
          </form>
        )}

        {step === STEP_RATES && (
          <div>
            <div className="border rounded p-2 mb-3 bg-light d-flex justify-content-between align-items-center">
              <div className="small text-muted">
                📦 {formData.length}×{formData.width}×{formData.height} cm
                &nbsp;|&nbsp; ⚖️ {formData.weight}g &nbsp;|&nbsp;
                {formData.paymentMode.toUpperCase()}
              </div>
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={() => {
                  setStep(STEP_FORM);
                  setSelectedRate(null);
                }}
              >
                Edit
              </button>
            </div>

            <h6 className="mb-3">
              {rates.length} Rate{rates.length !== 1 ? "s" : ""} Available
            </h6>

            <div className="d-flex flex-column gap-2 mb-4">
              {rates.map((rate, idx) => {
                const isSelected =
                  String(selectedRate?.rateId) === String(rate.rateId);

                return (
                  <RateCard
                    key={rate.rateId || rate.id || idx}
                    rate={rate}
                    isSelected={isSelected}
                    onSelect={() => setSelectedRate(rate)}
                  />
                );
              })}
            </div>

            {/* Create Shipment Button */}
            <button
              className="btn btn-success w-100"
              onClick={handleCreateShipment}
              disabled={isLoading || !selectedRate}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creating Shipment...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="me-2" />
                  Create Shipment
                  {selectedRate && (
                    <span className="ms-2 opacity-75 small">
                      via {selectedRate.courierName || selectedRate.name}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        )}

        {step === STEP_DONE && (
          <>
            <div className="border rounded p-2 mb-3 bg-light d-flex justify-content-between align-items-center">
              <div className="small text-muted">
                📦 {formData.length}×{formData.width}×{formData.height} cm
                &nbsp;|&nbsp; ⚖️ {formData.weight}g &nbsp;|&nbsp;
                {formData.paymentMode.toUpperCase()}
              </div>

              {trackingData?.tracking?.status !== "Delivered" && (
                <button
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => {
                    setStep(STEP_FORM);
                    setSelectedRate(null);
                  }}
                >
                  Edit
                </button>
              )}
            </div>

            {item?.statusHistoryAwb ? (
              <div>
                <h5 className="mb-3 text-success">📦 Shipment Details</h5>

                <div className="border rounded p-3 mb-3">
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong>AWB:</strong> {item?.statusHistoryAwb?.awb}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Delivery Partner:</strong>{" "}
                        {item?.statusHistoryAwb?.courierName}
                      </p>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      <p>
                        <strong>Provider:</strong>{" "}
                        {item?.statusHistoryAwb?.providerName}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Status:</strong>
                        <span className="text text-primary ms-2">
                          {trackingData?.tracking?.status}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      {item?.statusHistoryAwb?.trackingUrl && (
                        <a
                          href={item?.statusHistoryAwb?.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          Track Shipment
                        </a>
                      )}
                    </div>
                    <div className="col-md-6">
                      {item?.orderId && (
                        <Link
                          className="btn btn-sm btn-outline-primary mb-3 me-2"
                          to={`/orders/label/${item.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Print Shipment Label
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                <h6 className="mb-2">Tracking History</h6>

                {trackingLogs.length === 0 ? (
                  <p className="text-muted small">No tracking updates yet</p>
                ) : (
                  <div className="timeline">
                    {trackingLogs.map((log, idx) => (
                      <div className="card" key={idx}>
                        <div className="card-body">
                          <div key={idx}>
                            <div className="fw-bold small">{log.status}</div>
                            <div className="text-muted small">
                              {log.location}
                            </div>
                            {log?.timestamp && (
                              <div className="text-muted small">
                                {new Date(log?.timestamp).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5">
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    background: "#dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <CheckCircle size={32} color="#16a34a" />
                </div>
                <h5 className="text-success mb-1">Shipment Created!</h5>
                <p className="text-muted small">
                  Shipment has been successfully booked with{" "}
                  {selectedRate?.courierName || selectedRate?.name}. The order
                  list is being updated.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function RateCard({ rate, isSelected, onSelect }) {
  const price = rate.charges?.totalCharge ?? rate.finalRate ?? rate.rate ?? 0;
  const codCharge = rate.charges?.codCharge || 0;
  const eta = rate.estimatedDays
    ? `${rate.estimatedDays} days`
    : rate.estimatedDelivery || "";
  const provider = rate.providerName || rate.provider || "";
  const isCOD = codCharge > 0;

  const getShipmentIcon = (name = "") => {
    const value = name.toLowerCase();

    if (value.includes("air")) {
      return <Plane size={20} color="#64748b" />;
    }

    if (value.includes("surface")) {
      return <Truck size={20} color="#64748b" />;
    }

    return <Package size={20} color="#64748b" />; // default
  };

  return (
    <div
      onClick={onSelect}
      style={{ cursor: "pointer" }}
      className={`border rounded p-3 d-flex align-items-center gap-3 ${
        isSelected
          ? "border-success bg-success bg-opacity-10"
          : "border-light-subtle"
      }`}
    >
      {/* Radio */}
      <div
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          border: `2px solid ${isSelected ? "#16a34a" : "#ced4da"}`,
          background: isSelected ? "#16a34a" : "transparent",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isSelected && (
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "#fff",
            }}
          />
        )}
      </div>
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "8px",
          background: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {rate?.image ? (
          <img
            src={rate?.image}
            alt="courier"
            style={{ objectFit: "contain" }}
          />
        ) : (
          getShipmentIcon(rate?.courierName || rate?.name)
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fw-bold small text-dark">{rate?.courierName}</div>
        <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
          {provider && (
            <span
              className="badge bg-secondary bg-opacity-10 text-secondary"
              style={{ fontSize: "10px" }}
            >
              {provider}
            </span>
          )}
          {eta && (
            <span className="text-muted" style={{ fontSize: "11px" }}>
              🕐 {eta}
            </span>
          )}
          {isCOD && (
            <span
              className="badge bg-warning text-dark"
              style={{ fontSize: "10px" }}
            >
              COD
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="text-end" style={{ minWidth: "80px" }}>
        <div className="fw-bold small text-dark">
          Total Charges: ₹{rate.charges?.totalCharge ?? price}
        </div>
      </div>
    </div>
  );
}

// ─── Step Badge ────────────────────────────────────────────────────────
function StepBadge({ n, label, active, done }) {
  const bg = done ? "#16a34a" : active ? "#0d6efd" : "#e2e8f0";
  const color = done || active ? "#fff" : "#94a3b8";
  const textColor = done ? "#16a34a" : active ? "#0d6efd" : "#94a3b8";

  return (
    <div className="d-flex align-items-center gap-1">
      <div
        style={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: bg,
          color,
          fontSize: "11px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.2s",
        }}
      >
        {done ? "✓" : n}
      </div>
      <span
        style={{
          fontSize: "12px",
          color: textColor,
          fontWeight: active ? "600" : "400",
        }}
      >
        {label}
      </span>
    </div>
  );
}

export default ShipmentDetail;
